import { useChannelStore } from '@entities/channel'
import { useJWTTokensStore } from '@shared/auth'
import type { RefreshResponse } from '@shared/auth/api/types'
import { useAuthStore } from '@shared/auth/model/store/auth-store'
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
    _retry?: boolean
}

export const httpClient: AxiosInstance = axios.create({
    baseURL: '/api',
    withCredentials: true
})

let isRefreshing = false
let failedQueue: {
    resolve: (value: AxiosResponse | undefined) => void
    reject: (reason: AxiosError) => void
}[] = []

const processQueue = (error: AxiosError | null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve(undefined)
        }
    })

    failedQueue = []
}

httpClient.interceptors.request.use(
    (config: ExtendedAxiosRequestConfig) => {
        const access = useJWTTokensStore.getState().access
        const channelId = useChannelStore.getState().current?.id
        config.headers.Authorization = config.withCredentials && access ? `Bearer ${access}` : ''
        config.headers['X-Channel-ID'] = channelId
        return config
    },
    (error) => Promise.reject(error)
)

httpClient.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig
        if (!originalRequest) return Promise.reject(error)

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                })
            }

            originalRequest._retry = true
            isRefreshing = true

            try {
                const oldRefresh = useJWTTokensStore.getState().refresh
                const {
                    data: { access, refresh }
                } = await axios.post<RefreshResponse>('/api/auth/refresh', { refresh: oldRefresh })

                useJWTTokensStore.getState().setAccess(access)
                useJWTTokensStore.getState().setRefresh(refresh)

                processQueue(null)

                return httpClient(originalRequest)
            } catch (refreshError) {
                useAuthStore.getState().setIsAuth(false)
                processQueue(refreshError as AxiosError)

                return Promise.reject(refreshError)
            } finally {
                isRefreshing = false
            }
        }

        return Promise.reject(error)
    }
)
